/**
 * @file AgencyVisuals.jsx
 * @description Shared visual building blocks for the agency marketing pages, so each
 *   page can say less and show more:
 *   - ProofChips: short trust/proof chips (real facts only)
 *   - FlowSteps: numbered post -> match -> hire flow with a connector line
 *   - IconTiles: icon + title + one short line grid
 *   - DifferentialGrid: six mini-visual cards (map, fit, pipeline, messaging, reach, verified)
 *   - PartnerCard: Founding Partner program card (apply handler passed in)
 *   - IndeedVsAbacases: qualitative generic-job-board vs ABAcases scorecard
 *   - SampleApplicantCard: one realistic, fictional, "Sample"-labelled applicant (match %, badges, payers)
 *   - CasePreview: what providers see vs what stays private (progressive disclosure)
 *   - ReachRings: illustrative commute-radius diagram around a metro
 *   Copy lives in agencyVisuals.* locale keys; sample visuals are labelled as samples.
 * @module components/AgencyVisuals
 */
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { LockIcon, CheckIcon, MapPinIcon, DollarIcon, ClockIcon, HomeIcon, AwardIcon, NavigationIcon, StarIcon, ShieldIcon, BriefcaseIcon, GlobeIcon, InfoIcon, UsersIcon, MessageIcon, TrendingUpIcon } from "./Icons";
import { getAgencyPricing } from "../lib/agencyPricing";
import { useConfigVersion } from "../lib/useConfigVersion";
import { SampleBadge } from "./UI";

const NAVY = "#0F1024";
const INDIGO = "#4F46E5";
const BORDER = "rgba(226,232,240,0.9)";
const MID = "#475569";
const LIGHT = "#94A3B8";

/** The standard non-numeric hero chips for the agency pages (no supply counts). */
export function agencyChips(t) {
  return [
    { Icon: BriefcaseIcon, label: t("agencyVisuals.chipOnlyAba") },
    { Icon: GlobeIcon, label: t("agencyVisuals.chipBilingual") },
    { Icon: ShieldIcon, label: t("agencyVisuals.chipVerified") },
    { Icon: DollarIcon, label: t("agencyVisuals.chipNoFees") },
  ];
}

/** Short proof chips: [{ Icon, label }]. */
export function ProofChips({ items, dark, center }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: center ? "center" : "flex-start" }}>
      {items.map(({ Icon, label }) => (
        <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, background: dark ? "rgba(255,255,255,0.09)" : "#EEF2FF", color: dark ? "#E2E8F0" : NAVY, border: `1px solid ${dark ? "rgba(255,255,255,0.14)" : "#E0E7FF"}` }}>
          {Icon && <Icon size={13} color={dark ? "#6ee7b7" : INDIGO} />} {label}
        </span>
      ))}
    </div>
  );
}

/** Numbered step flow: [{ Icon, title, sub }]. */
export function FlowSteps({ steps, dark, isMobile }) {
  const fg = dark ? "#fff" : NAVY;
  const sub = dark ? "rgba(255,255,255,0.6)" : MID;
  return (
    <div style={{ position: "relative", display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${steps.length}, 1fr)`, gap: isMobile ? 18 : 12 }}>
      {!isMobile && (
        <div aria-hidden="true" style={{ position: "absolute", top: 28, left: `${50 / steps.length}%`, right: `${50 / steps.length}%`, borderTop: `2px dashed ${dark ? "rgba(110,231,183,0.45)" : "#C7D2FE"}` }} />
      )}
      {steps.map(({ Icon, title, sub: s }, i) => (
        <div key={title} style={{ position: "relative", display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: "center", gap: isMobile ? 14 : 10, textAlign: isMobile ? "left" : "center" }}>
          <div style={{ position: "relative", width: 56, height: 56, borderRadius: "50%", flexShrink: 0, background: dark ? "#13183a" : "#fff", border: `2px solid ${dark ? "rgba(110,231,183,0.6)" : INDIGO}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: dark ? "0 0 0 6px rgba(16,185,129,0.08)" : "0 0 0 6px #EEF2FF" }}>
            <Icon size={22} color={dark ? "#6ee7b7" : INDIGO} />
            <span style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", background: dark ? "#6ee7b7" : INDIGO, color: dark ? NAVY : "#fff", fontSize: 11, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
          </div>
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: fg }}>{title}</div>
            {s && <div style={{ fontSize: 13, color: sub, marginTop: 3, lineHeight: 1.45 }}>{s}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Icon tile grid: [{ Icon, title, sub }]. */
export function IconTiles({ items, columns = 4, dark, isMobile, mobileColumns = 2 }) {
  // Phones: 2 columns by default; pass mobileColumns={1} when the tile text is long enough to wrap to 3 or 4 words a line.
  const mobileCols = items.some((x) => x.chip) ? 1 : mobileColumns;
  return (
    <div data-testid="icon-tiles" style={{ display: "grid", gridTemplateColumns: isMobile ? `repeat(${mobileCols}, minmax(0, 1fr))` : `repeat(${columns}, 1fr)`, gap: isMobile ? 10 : 14 }}>
      {items.map(({ Icon, title, sub, chip }) => (
        <div key={title} style={{ minWidth: 0, background: dark ? "rgba(255,255,255,0.05)" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,0.12)" : BORDER}`, borderRadius: 14, padding: isMobile ? "14px 12px" : "18px 16px", boxShadow: dark ? "none" : "0 1px 4px rgba(15,23,42,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: dark ? "rgba(16,185,129,0.22)" : "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon size={18} color={dark ? "#6ee7b7" : INDIGO} />
            </div>
            {chip && <span style={{ fontSize: 10.5, fontWeight: 800, color: dark ? "#6ee7b7" : INDIGO, background: dark ? "rgba(110,231,183,0.12)" : "#EEF2FF", borderRadius: 999, padding: "3px 8px", whiteSpace: "nowrap", fontFamily: "'JetBrains Mono', monospace" }}>{chip}</span>}
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: dark ? "#fff" : NAVY }}>{title}</div>
          {sub && <div style={{ fontSize: 12.5, color: dark ? "rgba(255,255,255,0.6)" : MID, marginTop: 4, lineHeight: 1.45 }}>{sub}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * A realistic, clearly fictional applicant as an agency sees it: initials, credential and BACB badges, match %,
 * what lines up (skills, setting, schedule, payer). Replaces grey skeleton bars on the agency pages.
 * Always labelled "Sample"; the name is obviously made up and no real person or agency is implied.
 */
export function SampleApplicantCard({ style }) {
  const { t } = useTranslation();
  const rows = [
    ["diffFitSkills", "sampleSkills"],
    ["diffFitSetting", "sampleSetting"],
    ["diffFitSchedule", "sampleSchedule"],
    ["diffFitPayer", "samplePayers"],
  ];
  return (
    <div data-testid="sample-applicant-card" style={{ position: "relative", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "16px 18px 14px", boxShadow: "0 8px 30px rgba(15,23,42,0.08)", fontFamily: T.fontBody, maxWidth: 440, width: "100%", boxSizing: "border-box", ...style }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <SampleBadge>{t("agencyVisuals.sample")}</SampleBadge>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: "50%", background: "#DBEAFE", color: "#1D4ED8", fontSize: 14, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{t("agencyVisuals.sampleInitials")}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 800, color: NAVY }}>{t("agencyVisuals.sampleName")}</div>
          <div style={{ fontSize: 12.5, color: MID, marginTop: 2 }}>{t("agencyVisuals.sampleMeta")}</div>
        </div>
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#065F46", lineHeight: 1, letterSpacing: "-0.02em" }}>92%</div>
          <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, letterSpacing: "0.05em", textTransform: "uppercase", marginTop: 3 }}>{t("agencyVisuals.sampleMatch")}</div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "12px 0 10px" }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: "#1D4ED8", background: "#DBEAFE", borderRadius: 999, padding: "4px 10px" }}>{t("agencyVisuals.sampleCred")}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 800, color: "#065F46", background: "#D1FAE5", borderRadius: 999, padding: "4px 10px" }}><ShieldIcon size={12} color="#065F46" /> {t("agencyVisuals.diffVerBacb")}</span>
      </div>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: "1px solid #F1F5F9", fontSize: 13 }}>
          <CheckIcon size={13} color={T.green} />
          <span style={{ color: MID, fontWeight: 700, width: 74, flexShrink: 0 }}>{t(`agencyVisuals.${label}`)}</span>
          <span style={{ color: NAVY, fontWeight: 600, minWidth: 0 }}>{t(`agencyVisuals.${value}`)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * What providers see vs what stays private. Sample values only.
 * `variant="provider"` speaks to the provider ("What you see up front") for provider pages such as the city job
 * pages: no pay figure is invented (it just says pay is shown up front) and the credential line uses `role`.
 */
export function CasePreview({ isMobile, area, variant = "agency", role, staff = false, roleKey }) {
  const { t } = useTranslation();
  const provider = variant === "provider";
  const areaText = area || t("agencyVisuals.previewAreaDefault");
  // Staff sample (BCBA and BCaBA city pages): the caseload and supervision those providers decide on, not an RBT
  // shift (persona review 2026-10-20).
  const shown = staff ? [
    { Icon: DollarIcon, label: t("agencyVisuals.previewPayShown") },
    { Icon: UsersIcon, label: t("agencyVisuals.previewStaffCaseload") },
    // A BCaBA both gets BCBA supervision and supervises RBTs, so the sample shows both (persona round 2).
    ...(roleKey === "bcaba"
      ? [{ Icon: AwardIcon, label: t("agencyVisuals.previewStaffSupervised") }, { Icon: UsersIcon, label: t("agencyVisuals.previewStaffSuperviseFew") }]
      : [{ Icon: AwardIcon, label: t("agencyVisuals.previewStaffSupervise") }, { Icon: ClockIcon, label: t("agencyVisuals.previewStaffHours") }]),
    { Icon: NavigationIcon, label: t("agencyVisuals.previewDistance", { area: areaText }) },
  ] : [
    { Icon: DollarIcon, label: provider ? t("agencyVisuals.previewPayShown") : t("agencyVisuals.previewPay") },
    { Icon: ClockIcon, label: t("agencyVisuals.previewSchedule") },
    { Icon: HomeIcon, label: t("agencyVisuals.previewSetting") },
    // A BT opening needs no certification, so the sample never says "BT required".
    { Icon: AwardIcon, label: roleKey === "bt" ? t("agencyVisuals.previewNoCert") : provider && role ? t("agencyVisuals.previewCredentialRole", { role }) : t("agencyVisuals.previewCredential") },
    { Icon: NavigationIcon, label: t("agencyVisuals.previewDistance", { area: areaText }) },
  ];
  const hidden = provider
    ? [t("agencyVisuals.previewHiddenProvider1"), t("agencyVisuals.previewHiddenProvider2"), t("agencyVisuals.previewHiddenProvider3")]
    : [t("agencyVisuals.previewHidden1"), t("agencyVisuals.previewHidden2"), t("agencyVisuals.previewHidden3")];
  const title = staff ? t("agencyVisuals.previewTitleStaff", { role }) : provider && role ? t("agencyVisuals.previewTitleRole", { role }) : t("agencyVisuals.previewTitle");
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: 16, maxWidth: 820, margin: "0 auto" }}>
      <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, padding: "20px 20px", boxShadow: "0 8px 30px rgba(15,23,42,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", color: T.green, textTransform: "uppercase" }}>{provider ? t("agencyVisuals.previewSeenProvider") : t("agencyVisuals.previewSeen")}</span>
          <SampleBadge>{t("agencyVisuals.sample")}</SampleBadge>
        </div>
        <div style={{ fontSize: 16, fontWeight: 900, color: NAVY, marginBottom: 12 }}>{title}</div>
        {shown.map(({ Icon, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid #F1F5F9` }}>
            <Icon size={15} color={INDIGO} /> <span style={{ fontSize: 13.5, color: NAVY, fontWeight: 600 }}>{label}</span>
            <span style={{ marginLeft: "auto", display: "inline-flex" }}><CheckIcon size={13} color={T.green} /></span>
          </div>
        ))}
      </div>
      <div style={{ background: "#F8FAFC", border: `1px dashed #CBD5E1`, borderRadius: 16, padding: "20px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", color: MID, textTransform: "uppercase", marginBottom: 14 }}>{t("agencyVisuals.previewLocked")}</div>
        {hidden.map((h) => (
          <div key={h} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 8, borderRadius: 10, background: "#fff", border: `1px solid ${BORDER}` }}>
            <LockIcon size={14} color={LIGHT} />
            <span style={{ fontSize: 13.5, color: MID, fontWeight: 600 }}>{h}</span>
          </div>
        ))}
        <div style={{ fontSize: 12.5, color: MID, marginTop: 10, lineHeight: 1.5 }}>{provider ? t("agencyVisuals.previewLockedNoteProvider") : t("agencyVisuals.previewLockedNote")}</div>
      </div>
    </div>
  );
}

/** Illustrative commute-radius diagram. towns: (string | {name, angle, outer})[]. */
export function ReachRings({ center, towns = [], isMobile, caption }) {
  const { t } = useTranslation();
  const W = isMobile ? 320 : 520, H = isMobile ? 260 : 340, cx = W / 2, cy = H / 2;
  const rings = [0.28, 0.52, 0.78].map((r) => r * (H / 2));
  return (
    <figure style={{ margin: 0, textAlign: "center" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, height: "auto" }} role="img" aria-label={caption || center}>
        {rings.map((r, i) => (
          <circle key={r} cx={cx} cy={cy} r={r} fill={i === 0 ? "rgba(79,70,229,0.10)" : "none"} stroke="#A5B4FC" strokeWidth="1.5" strokeDasharray={i ? "5 6" : "0"} />
        ))}
        {towns.map((town, i) => {
          // town: "Name" (spread evenly) or { name, angle, outer } for a rough compass
          // placement (degrees, 0 = east, -90 = north).
          const tw = typeof town === "string" ? { name: town } : town;
          const name = tw.name;
          const outer = typeof town === "string" ? i % 2 === 1 : Boolean(tw.outer);
          const ring = rings[outer ? 2 : 1];
          const a = (tw.angle ?? (-90 + (360 / towns.length) * i)) * (Math.PI / 180);
          const x = cx + Math.cos(a) * ring, y = cy + Math.sin(a) * ring;
          const anchor = Math.abs(Math.cos(a)) < 0.3 ? "middle" : Math.cos(a) > 0 ? "start" : "end";
          return (
            <g key={name}>
              <circle cx={x} cy={y} r="5" fill="#10B981" stroke="#fff" strokeWidth="2" />
              <text x={x + (anchor === "start" ? 9 : anchor === "end" ? -9 : 0)} y={y + (anchor === "middle" ? (Math.sin(a) < 0 ? -10 : 18) : 4)} textAnchor={anchor} fontSize={isMobile ? 10.5 : 12} fontWeight="700" fill={NAVY} fontFamily="Inter, sans-serif">{name}</text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r="11" fill={INDIGO} stroke="#fff" strokeWidth="3" />
        <text x={cx} y={cy + 30} textAnchor="middle" fontSize={isMobile ? 12 : 14} fontWeight="900" fill={NAVY} fontFamily="Inter, sans-serif">{center}</text>
      </svg>
      <figcaption style={{ fontSize: 12.5, color: MID, marginTop: 6 }}>
        <MapPinIcon size={12} color={INDIGO} /> {caption || t("agencyVisuals.reachCaption")}
      </figcaption>
    </figure>
  );
}

const MARK = {
  yes: { sym: "✓", color: "#059669", bg: "#ECFDF5" },
  no: { sym: "✕", color: "#DC2626", bg: "#FEF2F2" },
  partial: { sym: "~", color: "#B45309", bg: "#FFFBEB" },
};

/**
 * Channel comparison grid. columns: [{ title, sub, highlight }];
 * rows: [{ label, values: ("yes"|"no"|"partial"|string)[] }]. Compact icon grid on mobile (uses column.short).
 */
export function Scorecard({ columns, rows, isMobile }) {
  const cell = (v) => {
    const m = MARK[v];
    return m
      ? <span aria-label={v} style={{ display: "inline-flex", width: isMobile ? 22 : 28, height: isMobile ? 22 : 28, borderRadius: "50%", alignItems: "center", justifyContent: "center", background: m.bg, color: m.color, fontWeight: 900, fontSize: isMobile ? 12 : 14 }}>{m.sym}</span>
      : <span style={{ fontSize: isMobile ? 10 : 13, fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>{v}</span>;
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? `minmax(0, 1fr) repeat(${columns.length}, 54px)` : `1.3fr repeat(${columns.length}, 1fr)`, border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden", background: "#fff", boxShadow: "0 8px 30px rgba(15,23,42,0.06)" }}>
      <div style={{ padding: isMobile ? 8 : 16 }} />
      {columns.map((c) => (
        <div key={c.title} style={{ padding: isMobile ? "10px 3px" : "16px 12px", textAlign: "center", background: c.highlight ? INDIGO : "#F8FAFC", color: c.highlight ? "#fff" : NAVY, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: isMobile ? 10 : 14, fontWeight: 900, lineHeight: 1.2 }}>{isMobile ? (c.short || c.title) : c.title}</div>
          {c.sub && !isMobile && <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 2 }}>{c.sub}</div>}
        </div>
      ))}
      {rows.map((r) => (
        <div key={r.label} style={{ display: "contents" }}>
          <div style={{ padding: isMobile ? "10px 10px" : "13px 16px", fontSize: isMobile ? 12 : 13.5, fontWeight: 700, color: NAVY, borderTop: `1px solid ${BORDER}` }}>{r.label}</div>
          {r.values.map((v, i) => (
            <div key={i} style={{ padding: isMobile ? "8px 2px" : "11px 8px", textAlign: "center", borderTop: `1px solid ${BORDER}`, background: columns[i].highlight ? "#F5F7FF" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>{cell(v)}</div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Region coverage list: the region names, each with a pin. No tiers or status labels (owner rule: no "early" or "growing" signals). */
export function RegionList({ regions }) {
  return (
    <div style={{ display: "grid", gap: 8, maxWidth: 420, width: "100%" }}>
      {regions.map((name) => (
        <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", borderRadius: 12, background: "#fff", border: `1px solid ${BORDER}`, fontSize: 14, fontWeight: 800, color: NAVY }}>
          <MapPinIcon size={14} color={INDIGO} /> {name}
        </div>
      ))}
    </div>
  );
}

// ── Differential grid ────────────────────────────────────────────────────────
// Six cards, each with a small illustrative mini-visual (inline SVG / CSS) so the
// differences read at a glance. Everything drawn is illustrative ("Sample"): no real
// counts, names or provider data.

export const SAMPLE_TAG = { position: "absolute", top: 8, left: 8, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: LIGHT, background: "rgba(255,255,255,0.85)", borderRadius: 999, padding: "2px 7px" };

function MiniMap() {
  return (
    <svg viewBox="0 0 340 134" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="340" height="134" fill="#E8EEF9" />
      <path d="M-5 92 C60 70 120 108 200 80 S300 64 345 74" stroke="#fff" strokeWidth="10" fill="none" />
      <path d="M96 -5 C108 36 84 80 112 140" stroke="#fff" strokeWidth="8" fill="none" />
      <path d="M236 -5 C226 36 252 70 246 140" stroke="#fff" strokeWidth="6" fill="none" />
      <rect x="18" y="16" width="48" height="30" rx="6" fill="#D7E6D3" />
      <rect x="270" y="92" width="52" height="28" rx="6" fill="#D7E6D3" />
      <circle cx="150" cy="70" r="46" fill="rgba(79,70,229,0.10)" stroke="#6366F1" strokeWidth="1.5" strokeDasharray="5 5" />
      {[[122, 44], [180, 50], [132, 100], [176, 96], [284, 52]].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="6" fill="#10B981" stroke="#fff" strokeWidth="2" />
      ))}
      <circle cx="150" cy="70" r="8" fill={INDIGO} stroke="#fff" strokeWidth="3" />
      <g transform="translate(206 14)">
        <rect width="104" height="40" rx="8" fill="#fff" stroke="#E2E8F0" />
        <rect x="10" y="10" width="52" height="6" rx="3" fill={NAVY} opacity="0.8" />
        <rect x="10" y="23" width="36" height="5" rx="2.5" fill="#CBD5E1" />
        <rect x="72" y="20" width="22" height="11" rx="5.5" fill="#D1FAE5" />
      </g>
    </svg>
  );
}

function MiniFit({ t }) {
  const rows = ["diffFitSkills", "diffFitSetting", "diffFitSchedule", "diffFitPayer"];
  return (
    <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "8px 10px", width: "82%", boxShadow: "0 4px 14px rgba(15,23,42,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: NAVY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("agencyVisuals.sampleName")}</span>
        <span style={{ fontSize: 11, fontWeight: 900, color: "#065F46", background: "#D1FAE5", borderRadius: 999, padding: "2px 8px" }}>92%</span>
      </div>
      {rows.map((k) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: MID, lineHeight: 1.55 }}>
          <CheckIcon size={11} color={T.green} /> {t(`agencyVisuals.${k}`)}
        </div>
      ))}
    </div>
  );
}

function MiniPipeline({ t }) {
  const cols = [["diffPipeReviewing", "#D97706", 3], ["diffPipeInterview", "#2563EB", 2], ["diffPipeOffer", "#F59E0B", 1], ["diffPipeHired", "#0A6B4E", 1]];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6, width: "92%" }}>
      {cols.map(([k, color, n]) => (
        <div key={k} style={{ background: "#fff", borderRadius: 8, border: `1px solid ${BORDER}`, padding: "5px 4px 6px", minWidth: 0 }}>
          <div style={{ fontSize: 8.5, fontWeight: 800, color, textAlign: "center", marginBottom: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(`agencyVisuals.${k}`)}</div>
          {Array.from({ length: n }).map((_, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 3, background: "#F8FAFC", borderRadius: 5, padding: "3px 3px", marginBottom: 3 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <span style={{ height: 3.5, flex: 1, borderRadius: 2, background: "#CBD5E1" }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MiniChat({ t }) {
  const bubble = (side, text) => (
    <div style={{ alignSelf: side === "me" ? "flex-end" : "flex-start", maxWidth: "78%", fontSize: 11, fontWeight: 600, lineHeight: 1.3, padding: "5px 10px", borderRadius: side === "me" ? "12px 12px 3px 12px" : "12px 12px 12px 3px", background: side === "me" ? INDIGO : "#fff", color: side === "me" ? "#fff" : NAVY, border: side === "me" ? "none" : `1px solid ${BORDER}` }}>{text}</div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, width: "88%" }}>
      {bubble("them", t("agencyVisuals.diffMsg1"))}
      {bubble("me", t("agencyVisuals.diffMsg2"))}
      {bubble("them", t("agencyVisuals.diffMsg3"))}
    </div>
  );
}

function MiniFunnel({ t }) {
  const bars = [["diffReachEligible", "100%", "#C7D2FE"], ["diffReachNearby", "72%", "#818CF8"], ["diffReachStrong", "44%", INDIGO]];
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, width: "86%" }}>
      {bars.map(([k, w, bg]) => (
        <div key={k} style={{ width: w, background: bg, borderRadius: 7, padding: "5px 6px", textAlign: "center", fontSize: 10.5, fontWeight: 800, color: bg === INDIGO || bg === "#818CF8" ? "#fff" : NAVY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(`agencyVisuals.${k}`)}</div>
      ))}
    </div>
  );
}

function MiniVerified({ t }) {
  const badge = (label, color, bg) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 800, color, background: bg, borderRadius: 999, padding: "5px 10px", whiteSpace: "nowrap" }}>
      <ShieldIcon size={12} color={color} /> {label}
    </span>
  );
  return (
    <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", width: "82%", boxShadow: "0 4px 14px rgba(15,23,42,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
        <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: "50%", background: "#DBEAFE", color: "#1D4ED8", fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{t("agencyVisuals.sampleInitials")}</span>
        <span style={{ display: "grid", gap: 1, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: NAVY, whiteSpace: "nowrap" }}>{t("agencyVisuals.sampleName")}</span>
          <span style={{ fontSize: 10.5, fontWeight: 600, color: MID }}>{t("agencyVisuals.sampleCredBcba")}</span>
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {badge(t("agencyVisuals.diffVerBacb"), "#065F46", "#D1FAE5")}
      </div>
    </div>
  );
}

const DIFF_ICONS = { map: MapPinIcon, fit: StarIcon, pipeline: UsersIcon, messaging: MessageIcon, reach: TrendingUpIcon, verified: ShieldIcon };

const DIFFS = {
  map: { Visual: MiniMap, bare: true },
  fit: { Visual: MiniFit },
  pipeline: { Visual: MiniPipeline },
  messaging: { Visual: MiniChat },
  reach: { Visual: MiniFunnel },
  verified: { Visual: MiniVerified },
};

/**
 * Six differentiator cards with mini-visuals. `only` limits/reorders the cards (keys of
 * map | fit | pipeline | messaging | reach | verified); `compact` shrinks the visual area.
 * Responsive via auto-fit: 3 columns desktop, 2 tablet, 1 phone (no media queries needed).
 */
export function DifferentialGrid({ only, compact, sample, isMobile }) {
  const { t } = useTranslation();
  const keys = only || ["map", "fit", "pipeline", "messaging", "reach", "verified"];
  if (sample) {
    // `sample`: one realistic "Sample" applicant card beside plain feature tiles (no grey skeleton mini-visuals).
    return (
      <div data-testid="differential-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 0.9fr) minmax(0, 1.1fr)", gap: isMobile ? 18 : 28, alignItems: "center" }}>
        <div style={{ display: "flex", justifyContent: "center" }}><SampleApplicantCard /></div>
        <IconTiles isMobile={isMobile} columns={2} mobileColumns={1} items={keys.map((k) => ({ Icon: DIFF_ICONS[k], title: t(`agencyVisuals.diff_${k}_title`), sub: t(`agencyVisuals.diff_${k}_line`) }))} />
      </div>
    );
  }
  return (
    <div data-testid="differential-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 16 }}>
      {keys.map((k) => {
        const { Visual, bare } = DIFFS[k];
        return (
          <div key={k} style={{ minWidth: 0, background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 10px rgba(15,23,42,0.05)" }}>
            <div style={{ position: "relative", height: compact ? 112 : 134, background: bare ? "#E8EEF9" : "linear-gradient(180deg, #F5F7FF 0%, #EEF2FF 100%)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              <Visual t={t} />
              <span style={SAMPLE_TAG}>{t("agencyVisuals.sample")}</span>
            </div>
            <div style={{ padding: "14px 16px 16px" }}>
              <div style={{ fontSize: 15.5, fontWeight: 900, color: NAVY }}>{t(`agencyVisuals.diff_${k}_title`)}</div>
              <div style={{ fontSize: 13, color: MID, marginTop: 3, lineHeight: 1.45 }}>{t(`agencyVisuals.diff_${k}_line`)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Outside Florida note ─────────────────────────────────────────────────────

/**
 * The "Hiring anywhere in the US?" note as one string. The last sentence only
 * appears while the launch promo is active. `t` may be a fixed-language t (the
 * small-agency FAQ JSON-LD stays English).
 */
export function outsideFloridaText(t, isPromoActive) {
  const body = t("agencyVisuals.outsideFlBody");
  return isPromoActive ? `${body} ${t("agencyVisuals.outsideFlPromo")}` : body;
}

/** Info card for agencies hiring where ABAcases is newer. Reads the promo state from Admin config. */
export function OutsideFloridaNote({ isMobile }) {
  useConfigVersion(); // promo state comes from Admin config; re-render once it loads
  const { t } = useTranslation();
  const { isPromoActive } = getAgencyPricing();
  return (
    <div data-testid="outside-florida-note" style={{ maxWidth: 720, margin: "28px auto 0", display: "flex", gap: 14, alignItems: "flex-start", background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 16, padding: isMobile ? "16px 16px" : "18px 22px", boxSizing: "border-box" }}>
      <span aria-hidden="true" style={{ flexShrink: 0, marginTop: 1, display: "flex" }}><InfoIcon size={20} color={INDIGO} /></span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t("agencyVisuals.outsideFlTitle")}</div>
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: "#334155" }}>{outsideFloridaText(t, isPromoActive)}</p>
      </div>
    </div>
  );
}

// ── Founding Partner card ────────────────────────────────────────────────────

/** Founding Partner program. Application goes through the host page's contact modal. */
export function PartnerCard({ onApply, isMobile }) {
  const { t } = useTranslation();
  const bullets = [t("agencyVisuals.partnerB1"), t("agencyVisuals.partnerB4"), t("agencyVisuals.partnerB2"), t("agencyVisuals.partnerB3")];
  return (
    <div data-testid="partner-card" style={{ maxWidth: 820, margin: "0 auto", background: `linear-gradient(135deg, ${NAVY} 0%, #1E1B4B 100%)`, borderRadius: 20, padding: isMobile ? "24px 20px" : "32px 36px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: isMobile ? 20 : 32, boxShadow: "0 14px 44px rgba(15,16,36,0.22)" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6ee7b7", marginBottom: 10 }}>
          <StarIcon size={13} color="#6ee7b7" /> {t("agencyVisuals.partnerEyebrow")}
        </div>
        <h3 style={{ fontSize: isMobile ? 21 : 25, fontWeight: 900, color: "#fff", margin: "0 0 14px", letterSpacing: "-0.02em" }}>{t("agencyVisuals.partnerTitle")}</h3>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 9 }}>
          {bullets.map((b) => (
            <li key={b} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14.5, fontWeight: 600, color: "#E2E8F0" }}>
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(16,185,129,0.25)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><CheckIcon size={11} color="#6ee7b7" /></span>
              {b}
            </li>
          ))}
        </ul>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "stretch" : "center", gap: 8, flexShrink: 0 }}>
        <button type="button" onClick={onApply} style={{ padding: "14px 26px", borderRadius: 12, border: "none", background: "#fff", color: NAVY, fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "'Inter', sans-serif", whiteSpace: "nowrap" }}>{t("agencyVisuals.partnerCta")}</button>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", textAlign: "center" }}>{t("agencyVisuals.partnerNote")}</span>
      </div>
    </div>
  );
}

// ── Indeed vs ABAcases ───────────────────────────────────────────────────────

/** Qualitative comparison with a generic job board (Indeed). No prices or numbers. Honest about audience size. */
export function IndeedVsAbacases({ isMobile }) {
  const { t } = useTranslation();
  const columns = [
    { title: t("agencyVisuals.vsGeneric"), short: t("agencyVisuals.vsGenericShort"), sub: t("agencyVisuals.vsGenericSub") },
    { title: "ABAcases", short: "ABAcases", sub: t("agencyVisuals.vsOursSub"), highlight: true },
  ];
  const rows = [
    ["vsRowAba", "no", "yes"],
    ["vsRowAudience", "yes", "partial"],
    ["vsRowVerified", "no", "yes"],
    ["vsRowAgencyNpi", "no", "yes"],
    ["vsRowMatch", "partial", "yes"],
    ["vsRowMap", "partial", "yes"],
    ["vsRowPrivacy", "no", "yes"],
    ["vsRowPipeline", "yes", "yes"],
    ["vsRowLang", "partial", "yes"],
  ].map(([k, a, b]) => ({ label: t(`agencyVisuals.${k}`), values: [a, b] }));
  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <Scorecard columns={columns} rows={rows} isMobile={isMobile} />
    </div>
  );
}

// ── Founder ──────────────────────────────────────────────────────────────────

const FOUNDER_LINKEDIN = "https://www.linkedin.com/in/victor-carpinelli";

/**
 * "Who's behind ABAcases" block: photo, name, short story, languages, and two
 * actions (message via the site's contact form, LinkedIn). No personal phone or
 * email on the page. `long` adds the extra paragraph used on /about-aba-cases.
 */
export function FounderCard({ onMessage, isMobile, long = false }) {
  const { t } = useTranslation();
  return (
    <div data-testid="founder-card" style={{ maxWidth: 820, margin: "0 auto", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 20, padding: isMobile ? "24px 20px" : "32px 36px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "center" : "flex-start", gap: isMobile ? 18 : 30, boxShadow: "0 10px 36px rgba(15,23,42,0.07)" }}>
      <img src="/founder-victor.jpg" alt={t("agencyVisuals.founderName")} width={128} height={128} loading="lazy" style={{ width: isMobile ? 112 : 128, height: isMobile ? 112 : 128, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "4px solid #EEF2FF" }} />
      <div style={{ minWidth: 0, textAlign: isMobile ? "center" : "left" }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: INDIGO, marginBottom: 6 }}>{t("agencyVisuals.founderEyebrow")}</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: NAVY }}>{t("agencyVisuals.founderName")}</div>
        <div style={{ fontSize: 13.5, color: MID, fontWeight: 600, marginBottom: 12 }}>{t("agencyVisuals.founderRole")}</div>
        <p style={{ fontSize: 15, color: NAVY, lineHeight: 1.65, margin: "0 0 10px" }}>“{t("agencyVisuals.founderQuote")}”</p>
        {long && <p style={{ fontSize: 14.5, color: MID, lineHeight: 1.65, margin: "0 0 10px" }}>{t("agencyVisuals.founderLong")}</p>}
        <div style={{ fontSize: 12.5, color: LIGHT, fontWeight: 700, marginBottom: 16 }}>{t("agencyVisuals.founderLanguages")}</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
          <button type="button" onClick={onMessage} style={{ padding: "11px 20px", borderRadius: 10, border: "none", background: INDIGO, color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>{t("agencyVisuals.founderMessage")}</button>
          <a href={FOUNDER_LINKEDIN} target="_blank" rel="noopener noreferrer" style={{ padding: "11px 20px", borderRadius: 10, border: `1.5px solid ${BORDER}`, color: NAVY, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>LinkedIn</a>
        </div>
      </div>
    </div>
  );
}
